import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Zap, Globe2, RefreshCcw, Search, ArrowRight, Star, CheckCircle2, ChevronDown } from "lucide-react";
import api, { fmtTL } from "@/lib/api";
import Seo from "@/components/Seo";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import HeroAnimation from "@/components/HeroAnimation";

function Stat({ value, label }) {
  return (
    <div className="text-center">
      <div className="font-head text-2xl sm:text-3xl font-extrabold text-brand-500">{value}</div>
      <div className="text-xs text-panel-secondary mt-1">{label}</div>
    </div>
  );
}

function ServiceCard({ s }) {
  return (
    <Link to={`/sms-onay/${s.code}`} data-testid={`home-service-${s.code}`}
      className="card p-4 flex flex-col items-center text-center gap-2 hover:-translate-y-1 hover:border-brand-500 transition-all duration-200 group relative">
      {s.popular && <span className="absolute top-2 right-2 text-[9px] font-bold bg-brand/15 text-brand-500 rounded-full px-2 py-0.5">Popüler</span>}
      <img src={s.icon} alt={s.name} className="h-11 w-11 rounded-xl object-contain bg-white p-1" loading="lazy" onError={(e) => (e.target.style.visibility = "hidden")} />
      <div className="text-sm font-semibold line-clamp-1">{s.name}</div>
      <div className="text-xs font-semibold text-brand-500 bg-brand/10 rounded-full px-2.5 py-1">{fmtTL(s.price_from)}'den</div>
    </Link>
  );
}

export default function Home() {
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const [openFaq, setOpenFaq] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/public/services").then(({ data }) => setServices(data.services || []));
    api.get("/public/settings").then(({ data }) => setSettings(data));
  }, []);

  const seo = settings?.seo || {};
  const faq = seo.faq || [];
  const filtered = services.filter((s) => s.name.toLowerCase().includes(q.toLowerCase())).slice(0, q ? 60 : 18);

  return (
    <div className="min-h-screen bg-panel-bg text-panel-text">
      <Seo title={seo.title} description={seo.description} keywords={seo.keywords} />
      <PublicHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 noise pointer-events-none" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-14 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-panel-border bg-panel-card px-3 py-1.5 text-xs font-semibold text-panel-secondary">
              <span className="h-2 w-2 rounded-full bg-emerald-500 blink" /> 180+ ülke · 700+ servis · canlı
            </span>
            <h1 className="mt-5 font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Anında <span className="text-brand-500">SMS Onay</span> ve<br />Sanal Numara Alma
            </h1>
            <p className="mt-5 text-base sm:text-lg text-panel-secondary max-w-xl leading-relaxed">
              WhatsApp, Telegram, Instagram ve yüzlerce servis için saniyeler içinde sanal numara alın.
              SMS gelmezse ücret otomatik iade. Güvenilir, ucuz ve hızlı.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={() => nav("/kayit")} data-testid="hero-cta-register" className="btn-blue rounded-xl px-6 h-12 font-semibold flex items-center gap-2">
                Hemen Başla <ArrowRight size={18} />
              </button>
              <a href="#servisler" className="rounded-xl px-6 h-12 font-semibold flex items-center gap-2 card hover:border-brand-500 transition-colors">
                Servisleri Gör
              </a>
            </div>
            <div className="mt-10 grid grid-cols-4 gap-4 max-w-md">
              <Stat value="180+" label="Ülke" />
              <Stat value="700+" label="Servis" />
              <Stat value="20dk" label="Geçerlilik" />
              <Stat value="%99" label="Başarı" />
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.1 }}>
            <HeroAnimation />
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { Icon: Zap, t: "Anında Teslim", d: "Numaralar saniyeler içinde tahsis edilir." },
            { Icon: ShieldCheck, t: "İade Garantisi", d: "SMS gelmezse ücretiniz otomatik iade." },
            { Icon: Globe2, t: "180+ Ülke", d: "Dünya genelinde geniş numara havuzu." },
            { Icon: RefreshCcw, t: "7/24 Çalışır", d: "Kesintisiz otomatik sistem." },
          ].map(({ Icon, t, d }, i) => (
            <motion.div key={t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="card p-5">
              <div className="h-11 w-11 rounded-xl bg-brand/10 text-brand-500 grid place-items-center mb-3"><Icon size={22} /></div>
              <div className="font-head font-semibold">{t}</div>
              <div className="text-sm text-panel-secondary mt-1">{d}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="servisler" className="mx-auto max-w-7xl px-4 sm:px-6 mt-16">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">Servis Seç</h2>
            <p className="text-panel-secondary text-sm mt-1">SMS onay almak istediğin servisi seç.</p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-panel-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="home-service-search"
              placeholder="Servis ara (WhatsApp, Telegram...)" className="pinput w-full rounded-xl h-11 pl-10 pr-4 text-sm outline-none focus:border-brand-500" />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          {filtered.map((s) => <ServiceCard key={s.code} s={s} />)}
        </div>
      </section>

      {/* FAQ */}
      <section id="sss" className="mx-auto max-w-3xl px-4 sm:px-6 mt-20">
        <h2 className="font-head text-2xl sm:text-3xl font-bold tracking-tight text-center">Sıkça Sorulan Sorular</h2>
        <p className="text-panel-secondary text-sm mt-1 text-center mb-8">SMS onay hakkında merak edilenler</p>
        <div className="space-y-3">
          {faq.map((f, i) => (
            <div key={i} className="card overflow-hidden" data-testid={`faq-item-${i}`}>
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 p-4 text-left font-semibold">
                {f.q}
                <ChevronDown size={18} className={`shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <div className="px-4 pb-4 text-sm text-panel-secondary leading-relaxed anim-fadein">{f.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* SEO BLOCK */}
      {seo.homepage_seo_html && (
        <section className="mx-auto max-w-4xl px-4 sm:px-6 mt-20">
          <article className="prose-sms text-panel-secondary leading-relaxed [&_h2]:font-head [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-panel-text [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:font-head [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-panel-text [&_h3]:mt-5 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mt-1 [&_strong]:text-panel-text"
            dangerouslySetInnerHTML={{ __html: seo.homepage_seo_html }} />
        </section>
      )}

      <PublicFooter keywords={seo.keywords || []} />
    </div>
  );
}
